import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { Box, Chip, Link, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ScannerCase } from 'models/scanner-case';
import { useNavigate } from 'react-router-dom';
import InfoText from 'components/shared/info-text.component';

type ScannerCaseInfoProps = {
    scannerCase: ScannerCase;
};
const ScannerCaseInfo: FC<ScannerCaseInfoProps> = ({ scannerCase }: ScannerCaseInfoProps) => {
    const { t: translate } = useTranslation(['scan-cases', 'orders', 'common']);
    const navigate = useNavigate();

    const getPatientName = () => {
        const name = scannerCase.patient?.firstName
            ? `${scannerCase.patient?.firstName} ${scannerCase.patient?.lastName || ''}`
            : translate('scan-cases:NO_PATIENT_NAME');
        return name;
    };

    const navigateToOrderDetails = (orderId: string) => {
        navigate(`/order-details/${orderId}/main-view`);
    };

    return (
        <>
            <Box mb={2} display="flex" alignItems="center" gap={1}>
                <Typography variant="h4">{scannerCase?.displayCaseId}</Typography>
                <Chip size="small" label={scannerCase?.externalSourceStatus} />
            </Box>
            {scannerCase.deliveryDate ? (
                <Box mb={2}>
                    {translate('orders:DELIVERY_DATE')} {formatDate(new Date(scannerCase.deliveryDate), 'datetime')}
                </Box>
            ) : undefined}

            <Box display="flex">
                <Paper variant="outlined" sx={{ p: 2, mr: 2, minWidth: 300 }}>
                    <Box>
                        <Box>
                            <Typography variant="h4">{translate('common:DENTIST')}</Typography>
                        </Box>
                        <Box>{scannerCase.dentist?.name || translate('orders:UNKNOWN')}</Box>
                        <Box>
                            <Typography variant="body2" color="grey.700">
                                {scannerCase.clinic?.name || translate('orders:UNKNOWN')}
                            </Typography>
                        </Box>
                        {scannerCase.dentist?.email ? (
                            <Box>
                                <Typography variant="body2" color="grey.700">
                                    {scannerCase.dentist?.email}
                                </Typography>
                            </Box>
                        ) : undefined}
                    </Box>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, mr: 2, minWidth: 300 }}>
                    <Box>
                        <Box>
                            <Typography variant="h4">{translate('common:PATIENT')}</Typography>
                        </Box>
                        <Box>{getPatientName()}</Box>
                        <Box>{scannerCase.patient?.patientRefNo || ''}</Box>
                    </Box>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, minWidth: 300 }}>
                    <Box>
                        <Box>
                            <Typography variant="h4">{translate('orders:ORDERS')}</Typography>
                        </Box>
                        <Box>
                            {scannerCase.orders.length > 0 ? (
                                <>
                                    {scannerCase.orders.map((order) => (
                                        <ListItem disablePadding key={order._id}>
                                            <Link
                                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                                onClick={() => navigateToOrderDetails(order._id)}
                                            >
                                                <ListItemText primary={order.orderId} />
                                            </Link>
                                        </ListItem>
                                    ))}
                                </>
                            ) : (
                                <InfoText text={translate('scan-cases:NO_ORDERS')} />
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default ScannerCaseInfo;
